import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from "react";
import * as signalR from "@microsoft/signalr";
import toastr from "toastr";
import "toastr/build/toastr.css";
import { API_WEB_URLS } from "../constants/constAPI";
import { useNavigate } from "react-router-dom";
import BookingAlertModal from "../components/CommonForBoth/BookingAlertModal";

const SignalRNotificationContext = createContext(null);

const STORAGE_KEY = "admin_booking_notifications";
const MAX_STORED_NOTIFICATIONS = 25;

// Safety error boundary so modal errors can never crash the admin screen into a blank page
class ModalErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidCatch(error, errorInfo) {
    console.error("❌ [SignalRNotification] ModalErrorBoundary caught an error:", error, errorInfo);
  }
  render() {
    if (this.state.hasError) {
      return null;
    }
    return this.props.children;
  }
}

// Synthesize an attention chime using Web Audio API (cross-browser, zero assets required)
const playNotificationChime = () => {
  try {
    const AudioContextClass = window.AudioContext || window.webkitAudioContext;
    if (!AudioContextClass) return;
    const ctx = new AudioContextClass();

    if (ctx.state === "suspended") {
      ctx.resume().catch(() => {});
    }

    const now = ctx.currentTime;

    // First Tone (D5 - 587.33 Hz)
    const osc1 = ctx.createOscillator();
    const gain1 = ctx.createGain();
    osc1.type = "sine";
    osc1.frequency.setValueAtTime(587.33, now);
    gain1.gain.setValueAtTime(0.25, now);
    gain1.gain.exponentialRampToValueAtTime(0.001, now + 0.35);
    osc1.connect(gain1);
    gain1.connect(ctx.destination);
    osc1.start(now);
    osc1.stop(now + 0.35);

    // Second Tone (A5 - 880 Hz) - Higher resonant note
    const osc2 = ctx.createOscillator();
    const gain2 = ctx.createGain();
    osc2.type = "sine";
    osc2.frequency.setValueAtTime(880, now + 0.14);
    gain2.gain.setValueAtTime(0.35, now + 0.14);
    gain2.gain.exponentialRampToValueAtTime(0.001, now + 0.65);
    osc2.connect(gain2);
    gain2.connect(ctx.destination);
    osc2.start(now + 0.14);
    osc2.stop(now + 0.65);
  } catch (err) {
    console.warn("[SignalRNotification] Audio chime failed:", err);
  }
};

export const SignalRNotificationProvider = ({ children }) => {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch {
      return [];
    }
  });

  const [unreadCount, setUnreadCount] = useState(0);
  const [isConnected, setIsConnected] = useState(false);
  const [alertModalOpen, setAlertModalOpen] = useState(false);
  const [activeAlertBooking, setActiveAlertBooking] = useState(null);

  const subscribersRef = useRef(new Set());
  const connectionRef = useRef(null);
  const lastAlertedBookingIdRef = useRef(null);

  // Subscribe callback for screens like AssignDriver.js
  const subscribeToNewBookings = useCallback((callback) => {
    subscribersRef.current.add(callback);
    return () => {
      subscribersRef.current.delete(callback);
    };
  }, []);

  const markAllAsRead = useCallback(() => {
    setUnreadCount(0);
  }, []);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch (e) {
      console.warn("Error clearing notifications storage:", e);
    }
  }, []);

  // Handle incoming booking from SignalR
  const handleNewBooking = useCallback(
    (bookingData) => {
      console.log("🚨 [SignalRNotification] New Booking Received:", bookingData);

      const bookingId = bookingData?.BookingId || bookingData?.bookingId || bookingData?.Id || "New";

      // Prevent duplicate rapid alerts for identical booking ID
      if (lastAlertedBookingIdRef.current && String(lastAlertedBookingIdRef.current) === String(bookingId)) {
        return;
      }
      lastAlertedBookingIdRef.current = String(bookingId);

      // Play sound chime
      playNotificationChime();

      const customerName = bookingData?.CustomerName || bookingData?.customerName || bookingData?.UserName || "Customer";
      const customerMobile = bookingData?.CustomerMobile || bookingData?.customerMobile || bookingData?.UserMobileNo || "";
      const pickup = bookingData?.PickupAddress || bookingData?.pickupAddress || "Pickup Location";
      const drop = bookingData?.DropAddress || bookingData?.dropAddress || bookingData?.Stop1Address || bookingData?.Stop1ReceiverHouseDetails || "Drop Location";
      const fare = bookingData?.Price ?? bookingData?.price ?? 0;
      const km = bookingData?.TotalTripKM ?? bookingData?.totalTripKM ?? 0;

      const vehicleType = 
        bookingData?.VehicleTypeName || 
        bookingData?.vehicleTypeName || 
        bookingData?.DemandedVehicleType || 
        bookingData?.demandedVehicleType || 
        bookingData?.VehicleType || 
        bookingData?.vehicleType || 
        "";

      const vehicleCategory = 
        bookingData?.VehicleCategoryName || 
        bookingData?.vehicleCategoryName || 
        bookingData?.VehicleCategory || 
        bookingData?.vehicleCategory || 
        "";

      const capacity = 
        bookingData?.WeightCapacity || 
        bookingData?.weightCapacity || 
        bookingData?.DemandedWeightCapacity || 
        bookingData?.demandedWeightCapacity || 
        bookingData?.WeightRangeTo || 
        "";

      const goodsType = 
        bookingData?.GoodsType || 
        bookingData?.goodsType || 
        "";

      const dateStr = bookingData?.BookingDate || new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

      const newNotification = {
        id: `noti-${Date.now()}-${bookingId}`,
        bookingId: String(bookingId),
        customerName,
        customerMobile,
        pickup,
        drop,
        fare,
        km,
        vehicleCategory,
        vehicleType: vehicleType || vehicleCategory,
        vehicleTypeName: vehicleType,
        capacity,
        goodsType,
        time: dateStr,
        timestamp: Date.now(),
        isRead: false,
      };

      // 1. Trigger the Center Modal Popup!
      setActiveAlertBooking(newNotification);
      setAlertModalOpen(true);

      // 2. Update state & persistence for topbar bell dropdown
      setNotifications((prev) => {
        const prevList = Array.isArray(prev) ? prev : [];
        const updated = [newNotification, ...prevList].slice(0, MAX_STORED_NOTIFICATIONS);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch (e) {
          console.warn("Failed to persist notification:", e);
        }
        return updated;
      });
      setUnreadCount((prev) => (typeof prev === "number" ? prev : 0) + 1);

      // 3. Notify all active page subscribers (e.g. AssignDriver to auto-fetch table)
      subscribersRef.current.forEach((cb) => {
        try {
          cb(bookingData);
        } catch (cbErr) {
          console.error("[SignalRNotification] Subscriber error:", cbErr);
        }
      });

      // 4. Secondary toastr notification
      toastr.options = {
        closeButton: true,
        debug: false,
        newestOnTop: true,
        progressBar: true,
        positionClass: "toast-top-right",
        preventDuplicates: true,
        onclick: () => {
          navigate("/AssignDriver");
        },
        showDuration: "300",
        hideDuration: "1000",
        timeOut: "10000",
        extendedTimeOut: "5000",
      };

      const pickupShort = pickup.length > 40 ? pickup.substring(0, 37) + "..." : pickup;
      const dropShort = drop.length > 40 ? drop.substring(0, 37) + "..." : drop;

      const toastHtml = `
        <div style="font-family: inherit; font-size: 12.5px; line-height: 1.4;">
          <div style="font-weight: 600; font-size: 13.5px; margin-bottom: 4px; color: #fff;">
            🚕 Ride #${bookingId} Booked!
          </div>
          <div>👤 <b>${customerName}</b> ${customerMobile ? `(${customerMobile})` : ""}</div>
          <div>📍 <b>Pickup:</b> ${pickupShort}</div>
          <div>🏁 <b>Drop:</b> ${dropShort}</div>
          <div>💰 <b>₹${fare}</b> (${km} KM)</div>
        </div>
      `;

      toastr.info(toastHtml, "New Booking Alert!");
    },
    [navigate]
  );

  // Expose manual test function in developer console for instant testing
  useEffect(() => {
    window.testBookingAlert = (customData) => {
      handleNewBooking(
        customData || {
          BookingId: Math.floor(1000 + Math.random() * 9000),
          CustomerName: "Rahul Sharma",
          CustomerMobile: "+91 9876543210",
          PickupAddress: "Sector 62, Noida, Uttar Pradesh",
          DropAddress: "Connaught Place, New Delhi",
          TotalTripKM: 24.5,
          Price: 480,
          VehicleCategory: "Tata Ace / Mini Truck",
          BookingDate: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        }
      );
    };
  }, [handleNewBooking]);

  // Initialize SignalR connection
  useEffect(() => {
    const hubUrl = API_WEB_URLS.BASE
      ? API_WEB_URLS.BASE.replace("/api/V1/", "/bookingHub").replace("/api/v1/", "/bookingHub")
      : "https://apikirayago.shinewellsofttech.co.in/bookingHub";

    console.log("🔌 [SignalRNotification] Connecting to:", hubUrl);

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl)
      .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    const registerOnHub = async () => {
      // 1. Join ActiveDrivers using RegisterDriver("0") - works on LIVE production server!
      try {
        await connection.invoke("RegisterDriver", "0");
        console.log("✅ [SignalRNotification] Registered in ActiveDrivers group (live fallback)");
      } catch (err) {
        console.warn("⚠️ [SignalRNotification] RegisterDriver fallback error:", err);
      }

      // 2. Also register as Admin in Admins group
      try {
        await connection.invoke("RegisterAdmin");
        console.log("✅ [SignalRNotification] Registered in Admins group");
      } catch (err) {
        console.warn("⚠️ [SignalRNotification] RegisterAdmin (pending deploy on server):", err);
      }
    };

    // Handle booking status update / driver acceptance
    const handleBookingAcceptedOrUpdated = (updatedBookingId) => {
      console.log(`🔄 [SignalRNotification] Booking ${updatedBookingId} updated/accepted`);
      const bIdStr = String(updatedBookingId || "");

      // Close modal cleanly outside state updaters
      setActiveAlertBooking((current) => {
        if (current && (!bIdStr || String(current.bookingId) === bIdStr)) {
          return null;
        }
        return current;
      });
      setAlertModalOpen(false);

      if (bIdStr) {
        toastr.success(`🚕 Ride #${bIdStr} has been accepted by a driver!`, "Ride Accepted");
      }

      // Remove accepted booking from active notifications list so it doesn't linger
      setNotifications((prev) => {
        const prevList = Array.isArray(prev) ? prev : [];
        const updated = prevList.filter((n) => String(n.bookingId) !== bIdStr);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch (_) {}
        return updated;
      });
      setUnreadCount((prev) => Math.max(0, (typeof prev === "number" ? prev : 1) - 1));

      // Notify page subscribers (AssignDriver, Dashboard) to refresh tables immediately!
      subscribersRef.current.forEach((cb) => {
        try {
          cb({ bookingId: updatedBookingId, isUpdated: true, isAccepted: true });
        } catch (e) {
          console.error(e);
        }
      });
    };

    // Handle booking cancellation
    const handleBookingCancelled = (cancelledBookingId) => {
      console.log(`❌ [SignalRNotification] Booking ${cancelledBookingId} cancelled`);
      const bIdStr = String(cancelledBookingId || "");

      setActiveAlertBooking((current) => {
        if (current && (!bIdStr || String(current.bookingId) === bIdStr)) {
          return null;
        }
        return current;
      });
      setAlertModalOpen(false);

      if (bIdStr) {
        toastr.warning(`🚕 Ride #${bIdStr} was cancelled`, "Ride Cancelled");
      }

      setNotifications((prev) => {
        const prevList = Array.isArray(prev) ? prev : [];
        const updated = prevList.filter((n) => String(n.bookingId) !== bIdStr);
        try {
          localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
        } catch (_) {}
        return updated;
      });
      setUnreadCount((prev) => Math.max(0, (typeof prev === "number" ? prev : 1) - 1));

      subscribersRef.current.forEach((cb) => {
        try {
          cb({ bookingId: cancelledBookingId, isUpdated: true, isCancelled: true });
        } catch (e) {
          console.error(e);
        }
      });
    };

    // Event 1: Full payload broadcast from updated backend
    connection.on("AdminNewBookingReceived", (data) => {
      handleNewBooking(data);
    });

    // Event 2: Booking updated / accepted
    connection.on("BookingUpdated", (bookingId) => {
      handleBookingAcceptedOrUpdated(bookingId);
    });

    // Event 3: Driver accepted booking
    connection.on("BookingAcceptedByDriver", (data) => {
      const bId = data?.BookingId || data?.bookingId || data;
      handleBookingAcceptedOrUpdated(bId);
    });

    // Event 3b: Booking cancelled
    connection.on("BookingCancelled", (data) => {
      const bId = data?.BookingId || data?.bookingId || data;
      handleBookingCancelled(bId);
    });

    // Event 4: General trigger broadcast from current live production server
    connection.on("NewBookingAvailable", async () => {
      console.log("🔔 [SignalRNotification] NewBookingAvailable trigger received from server");

      // Notify active page subscribers immediately (e.g. auto-refresh table in AssignDriver)
      subscribersRef.current.forEach((cb) => {
        try {
          cb({ isGeneralRefresh: true });
        } catch (e) {
          console.error(e);
        }
      });

      // Query latest booking from API so we have real details to show in the popup dialog!
      try {
        const base = API_WEB_URLS.BASE || "https://apikirayago.shinewellsofttech.co.in/api/V1/";
        const resp = await fetch(`${base}Masters/0/token/BookingMaster/Id/0`);
        if (resp.ok) {
          const resJson = await resp.json();
          const list = resJson?.data?.dataList || resJson?.dataList || [];
          if (list && list.length > 0) {
            const latest = list[0];
            const statusId = Number(latest.F_StatusMaster || latest.StatusId || 1);
            const hasDriver = !!latest.F_VehicleMaster || !!latest.DriverName || statusId > 1;

            // If driver has already accepted / assigned, do NOT show new booking alert!
            // Instead, dismiss alert modal if it was open for this booking!
            if (hasDriver) {
              console.log(`ℹ️ [SignalRNotification] Latest booking #${latest.Id} is already accepted/assigned (Status ${statusId}). Auto-dismissing alert modal.`);
              handleBookingAcceptedOrUpdated(latest.Id);
              return;
            }

            handleNewBooking({
              BookingId: latest.Id,
              CustomerName: latest.UserName || latest.SenderName || "Customer",
              CustomerMobile: latest.UserMobileNo || latest.SenderMobile || "",
              PickupAddress: latest.PickupAddress,
              DropAddress: latest.Stop1Address || latest.Stop1ReceiverHouseDetails || "",
              TotalTripKM: latest.TotalTripKM || 0,
              Price: latest.Price || 0,
              VehicleType: latest.VehicleTypeName || latest.DemandedVehicleType || "",
              VehicleTypeName: latest.VehicleTypeName || latest.DemandedVehicleType || "",
              VehicleCategory: latest.VehicleCategory || latest.VehicleCategoryName || "",
              VehicleCategoryName: latest.VehicleCategoryName || latest.VehicleCategory || "",
              DemandedWeightCapacity: latest.DemandedWeightCapacity || latest.WeightRangeTo || "",
              WeightCapacity: latest.DemandedWeightCapacity || latest.WeightRangeTo || "",
              GoodsType: latest.GoodsType || "",
              BookingDate: latest.DateOfCreation || new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
            });
          }
        }
      } catch (err) {
        console.warn("[SignalRNotification] Error fetching latest booking for popup alert:", err);
      }
    });

    connection.onreconnected(() => {
      console.log("🔄 [SignalRNotification] Reconnected to BookingHub");
      setIsConnected(true);
      registerOnHub();
    });

    connection.onreconnecting(() => {
      console.log("⚠️ [SignalRNotification] Reconnecting to BookingHub...");
      setIsConnected(false);
    });

    connection.onclose(() => {
      console.log("❌ [SignalRNotification] Connection closed");
      setIsConnected(false);
    });

    connection
      .start()
      .then(() => {
        setIsConnected(true);
        registerOnHub();
      })
      .catch((err) => {
        console.warn("⚠️ [SignalRNotification] Initial connection failed:", err);
      });

    connectionRef.current = connection;

    return () => {
      if (connectionRef.current) {
        connectionRef.current.stop();
      }
    };
  }, [handleNewBooking]);

  const value = {
    notifications,
    unreadCount,
    isConnected,
    markAllAsRead,
    clearNotifications,
    subscribeToNewBookings,
    triggerManualAlert: handleNewBooking,
  };

  return (
    <SignalRNotificationContext.Provider value={value}>
      {children}
      <ModalErrorBoundary>
        <BookingAlertModal
          isOpen={alertModalOpen}
          toggle={() => setAlertModalOpen((prev) => !prev)}
          booking={activeAlertBooking}
          onDismiss={() => setAlertModalOpen(false)}
        />
      </ModalErrorBoundary>
    </SignalRNotificationContext.Provider>
  );
};

export const useSignalRNotifications = () => {
  const context = useContext(SignalRNotificationContext);
  if (!context) {
    throw new Error("useSignalRNotifications must be used within a SignalRNotificationProvider");
  }
  return context;
};

export default SignalRNotificationContext;
